import React, { useEffect, useState } from 'react'

import { API, Card, Help } from '@enkla-gui'
import Modal from 'react-modal'
import { icons } from '@config'

export default function ReportExport({ model, fields, relationships, filters }) {
  const [showModal, setShowModal] = useState(false)
  const [exportType, setExportType] = useState('')
  const [filename, setFilename] = useState('')
  const [title, setTitle] = useState('')
  const [exporting, setExporting] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [modelLabel, setModelLabel] = useState('')

  // Get translated model label
  useEffect(() => {
    if (model) {
      API.get('api/report/models')
        .then(models => {
          const modelData = models.find(m => m.key === model)
          setModelLabel(modelData?.label || model)
        })
        .catch(() => setModelLabel(model))
    }
  }, [model])

  const openExportModal = (type) => {
    setExportType(type)
    // Use Swedish model name for filename
    const swedishModelName = (modelLabel || model).toLowerCase()
      .replace(/å/g, 'a')
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/\s+/g, '_')
    setFilename(`rapport_${swedishModelName}_${new Date().toISOString().split('T')[0]}`)
    setTitle(`Rapport - ${modelLabel || model}`)
    setShowModal(true)
    setShowDropdown(false)
  }

  const handleExport = async () => {
    if (!filename.trim()) {
      return
    }

    setExporting(true)

    try {
      const payload = {
        model,
        fields,
        relationships,
        filters,
        filename: filename.trim()
      }

      if (exportType === 'pdf') {
        payload.title = title.trim() || `Rapport - ${modelLabel || model}`
      }

      const endpoint = exportType === 'csv' ? 'api/report/export/csv' : 'api/report/export/pdf'

      // Handle file download
      const blob = await API.postBlob(endpoint, payload)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename + (exportType === 'csv' ? '.csv' : '.pdf')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      setShowModal(false)
    } catch (error) {
      console.error('Export error:', error)
      alert('Export misslyckades. Försök igen.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <div className='dropdown' style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <button
          className='btn btn-success dropdown-toggle'
          type='button'
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <i className={icons.download}></i> Exportera
        </button>
        {showDropdown && (
          <ul className='dropdown-menu show' style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
            display: 'block'
          }}>
            <li>
              <button
                className='dropdown-item'
                onClick={() => openExportModal('csv')}
              >
                <i className='fas fa-file-csv me-2'></i>
                Exportera som CSV
              </button>
            </li>
            <li>
              <button
                className='dropdown-item'
                onClick={() => openExportModal('pdf')}
              >
                <i className='fas fa-file-pdf me-2'></i>
                Exportera som PDF
              </button>
            </li>
          </ul>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        className='no-padding'
      >
        <Card
          collapsed={null}
          title={`Exportera som ${exportType?.toUpperCase()}`}
          icon={exportType === 'csv' ? 'fas fa-file-csv' : 'fas fa-file-pdf'}
          buttons={{
            header: [{ callback: () => setShowModal(false), icon: 'fas fa-times', text: 'Stäng' }],
            footer: [
              { callback: () => setShowModal(false), bg: 'light', text: 'Avbryt', disabled: exporting },
              { callback: handleExport, bg: 'success text-white', text: exporting ? 'Exporterar...' : `Exportera ${exportType?.toUpperCase()}`, disabled: !filename.trim() || exporting }
            ]
          }}
        >
          <div className='form-group'>
            <label>Filnamn</label>
            <input
              type='text'
              className='form-control'
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder='Ange filnamn...'
              disabled={exporting}
            />
            <small className='form-text text-muted'>
              Filändelsen ({exportType === 'csv' ? '.csv' : '.pdf'}) läggs till automatiskt.
            </small>
          </div>

          {exportType === 'pdf' && (
            <div className='form-group'>
              <label>Rapporttitel</label>
              <input
                type='text'
                className='form-control'
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder='Ange titel för rapporten...'
                disabled={exporting}
              />
              <small className='form-text text-muted'>
                Titeln visas överst i PDF-dokumentet.
              </small>
            </div>
          )}

          <Help type='info' title='Export information'>
            <ul className='mb-0'>
              <li>Datatyp: {modelLabel || model}</li>
              <li>Fält: {fields.length} st</li>
              <li>Relationer: {relationships.length} st</li>
              <li>Filter: {filters.length} st</li>
            </ul>
          </Help>
        </Card>
      </Modal>
    </>
  )
}
